import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GPTMetricsApi } from '../../services/api/GPTMetricsApi';
import { 
  MetricCard, 
  LoadingChart, 
  EmptyState, 
  DateRangePicker, 
  ExportButton, 
  InsightCard,
  ProgressBar
} from '../../components/analytics/SharedAnalyticsComponents';
import { 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * GptPromptEventsDashboard - Simplified AI Analytics (5-6 charts)
 * Cost efficiency, budget tracking, model comparison, token forecasting
 */
export default function GptPromptEventsDashboard() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 7 * 86400000);
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  });

  const [data, setData] = useState({
    summary: null,
    usageSeries: [],
    promptTypeDistribution: [],
    topUsers: [],
    documentUsage: [],
    efficiencySeries: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState({ monthly: 100, alert: 80 });
  const [showBudgetModal, setShowBudgetModal] = useState(false);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);

    try {
      const filters = { 
        from: dateRange.from, 
        to: dateRange.to,
        interval: 'day'
      };

      const [
        summary,
        usageSeries,
        promptTypes,
        topUsers,
        documentUsage,
        efficiencySeries
      ] = await Promise.all([
        GPTMetricsApi.getSummary(filters).then(r => r.ok ? r.json() : null).catch(() => null),
        GPTMetricsApi.getUsageSeries({ ...filters, perUser: false }).then(r => r.ok ? r.json() : []).catch(() => []),
        GPTMetricsApi.getPromptTypeDistribution(filters).then(r => r.ok ? r.json() : []).catch(() => []),
        GPTMetricsApi.getTopUsers({ ...filters, limit: 10 }).then(r => r.ok ? r.json() : []).catch(() => []),
        GPTMetricsApi.getDocumentUsage({ ...filters, limit: 15 }).then(r => r.ok ? r.json() : []).catch(() => []),
        GPTMetricsApi.getEfficiencySeries(filters).then(r => r.ok ? r.json() : []).catch(() => [])
      ]);

      setData({
        summary: summary || {},
        usageSeries: usageSeries || [],
        promptTypeDistribution: promptTypes || [],
        topUsers: topUsers || [],
        documentUsage: documentUsage || [],
        efficiencySeries: efficiencySeries || []
      });
    } catch (err) {
      setError(err);
      console.error('Failed to load GPT metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const calculateCostEfficiency = () => {
    if (!data.summary) return {};
    
    const costPerPrompt = (data.summary.totalCost || 0) / (data.summary.totalPrompts || 1);
    const costPerDocument = (data.summary.totalCost || 0) / Math.max(data.documentUsage.length, 1);
    const avgTokensPerPrompt = (data.summary.totalTokens || 0) / (data.summary.totalPrompts || 1);
    
    return {
      costPerPrompt: costPerPrompt.toFixed(4),
      costPerDocument: costPerDocument.toFixed(4),
      avgTokensPerPrompt: avgTokensPerPrompt.toFixed(0)
    };
  };

  const calculateBudgetStatus = () => {
    const currentCost = data.summary?.totalCost || 0;
    const percentUsed = (currentCost / budget.monthly) * 100;
    const daysInPeriod = Math.ceil((new Date(dateRange.to) - new Date(dateRange.from)) / (1000 * 60 * 60 * 24));
    const dailyBurn = currentCost / daysInPeriod;
    const projectedMonthly = dailyBurn * 30;
    
    return {
      current: currentCost,
      percentUsed: percentUsed.toFixed(1),
      dailyBurn: dailyBurn.toFixed(2),
      projectedMonthly: projectedMonthly.toFixed(2),
      isOverBudget: projectedMonthly > budget.monthly,
      isNearLimit: projectedMonthly > (budget.monthly * budget.alert / 100)
    };
  };

  const forecastTokenUsage = () => {
    if (data.usageSeries.length < 2) return[];
    
    // Simple linear forecast for next 7 days
    const recent = data.usageSeries.slice(-7);
    const avgDaily = recent.reduce((sum, day) => sum + (day.totalTokens || 0), 0) / recent.length;
    
    const forecast = [];
    const lastDate = new Date(data.usageSeries[data.usageSeries.length - 1].date);
    
    for (let i = 1; i <= 7; i++) {
      const forecastDate = new Date(lastDate);
      forecastDate.setDate(forecastDate.getDate() + i);
      forecast.push({
        date: forecastDate.toISOString(),
        totalTokens: Math.floor(avgDaily * (1 + (Math.random() * 0.2 - 0.1))), // ±10% variance
        forecast: true
      });
    }
    
    return [...data.usageSeries, ...forecast];
  };

  const generateInsights = () => {
    const insights = [];
    const efficiency = calculateCostEfficiency();
    const budgetStatus = calculateBudgetStatus();

    if (budgetStatus.isOverBudget) {
      insights.push({
        title: '⚠️ Budget Alert',
        description: `Projected monthly cost ($${budgetStatus.projectedMonthly}) exceeds budget ($${budget.monthly}). Current daily burn: $${budgetStatus.dailyBurn}/day.`,
        severity: 'danger'
      });
    } else if (budgetStatus.isNearLimit) {
      insights.push({
        title: '⚠️ Approaching Budget Limit',
        description: `${budgetStatus.percentUsed}% of monthly budget used. Daily burn rate: $${budgetStatus.dailyBurn}`,
        severity: 'warning'
      });
    }

    if (parseFloat(efficiency.costPerPrompt) > 0.05) {
      insights.push({
        title: 'High Cost Per Prompt',
        description: `Average $${efficiency.costPerPrompt} per prompt. Consider optimizing prompt length or using more efficient models.`,
        severity: 'warning'
      });
    }

    const topPromptType = data.promptTypeDistribution[0];
    if (topPromptType && data.promptTypeDistribution.length > 0) {
      const percentage = ((topPromptType.count / data.summary?.totalPrompts) * 100).toFixed(1);
      if (percentage > 60) {
        insights.push({
          title: 'Dominant Prompt Type',
          description: `${topPromptType.promptType} accounts for ${percentage}% of all prompts.`,
          severity: 'info'
        });
      }
    }

    return insights;
  };

  const efficiency = calculateCostEfficiency();
  const budgetStatus = calculateBudgetStatus();
  const forecastData = forecastTokenUsage();
  const insights = generateInsights();

  // Model comparison table data
  const modelComparison = [
    { model: 'gpt-4', avgCost: 0.06, avgTokens: 2000, efficiency: 33.3 },
    { model: 'gpt-3.5-turbo', avgCost: 0.002, avgTokens: 1500, efficiency: 750 },
    { model: 'claude-2', avgCost: 0.024, avgTokens: 1800, efficiency: 75 }
  ].map(m => ({
    ...m,
    efficiencyScore: (m.efficiency / 750 * 100).toFixed(0) // Normalize to percentage
  }));

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>GPT Prompt Analytics</h2>
        <EmptyState message="Admin access required" icon="🔒" />
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>🤖 GPT Prompt Analytics</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button 
            className="secondary"
            onClick={() => setShowBudgetModal(!showBudgetModal)}
          >
            💰 Budget Settings
          </button>
          <DateRangePicker 
            from={dateRange.from} 
            to={dateRange.to} 
            onChange={setDateRange}
          />
          <button onClick={loadData} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Budget Modal */}
      {showBudgetModal && (
        <div style={{ 
          marginBottom: '1.5rem', 
          background: 'var(--card-bg)', 
          padding: '1rem', 
          borderRadius: '12px', 
          boxShadow: 'var(--shadow-md)', 
          border: '2px solid #007bff' 
        }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Budget Configuration</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Monthly Budget ($)
              </label>
              <input
                type="number"
                value={budget.monthly}
                onChange={(e) => setBudget({ ...budget, monthly: parseFloat(e.target.value) || 0 })}
                style={{ width: '100%', fontSize: '.8rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Alert Threshold (%)
              </label>
              <input
                type="number"
                value={budget.alert}
                onChange={(e) => setBudget({ ...budget, alert: parseFloat(e.target.value) || 0 })}
                max="100"
                min="1"
                style={{ width: '100%', fontSize: '.8rem' }}
              />
            </div>
          </div>
          <button 
            onClick={() => setShowBudgetModal(false)}
            style={{ marginTop: '1rem', width: '100%' }}
          >
            Save & Close
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c' }}>
          Error loading data: {error.message}
        </div>
      )}

      {/* Insights */}
      {!loading && insights.length > 0 && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {insights.map((insight, idx) => (
            <InsightCard key={idx} {...insight} />
          ))}
        </div>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[1, 2, 3, 4, 5].map(i => <MetricCard key={i} loading />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard
            label="Total Prompts"
            value={data.summary?.totalPrompts?.toLocaleString() || '0'}
            subtitle={`${data.summary?.uniqueUsers || 0} users`}
            color="#007bff"
            sparklineData={data.usageSeries.slice(-7).map(v => ({ value: v.count || 0 }))}
          />
          <MetricCard
            label="Total Cost"
            value={`$${(data.summary?.totalCost || 0).toFixed(2)}`}
            subtitle={`Budget: $${budget.monthly} (${budgetStatus.percentUsed}%)`}
            color={budgetStatus.isOverBudget ? '#dc3545' : '#28a745'}
          />
          <MetricCard
            label="Cost/Prompt"
            value={`$${efficiency.costPerPrompt}`}
            subtitle="Average efficiency"
            color="#ffc107"
          />
          <MetricCard
            label="Total Tokens"
            value={(data.summary?.totalTokens || 0).toLocaleString()}
            subtitle={`${efficiency.avgTokensPerPrompt} avg/prompt`}
            color="#17a2b8"
          />
          <MetricCard
            label="Daily Burn Rate"
            value={`$${budgetStatus.dailyBurn}/day`}
            subtitle={`Projected: $${budgetStatus.projectedMonthly}/mo`}
            color={budgetStatus.isNearLimit ? '#ffc107' : '#6f42c1'}
          />
        </div>
      )}

      {/* Budget Progress */}
      <div style={{ marginBottom: '1.5rem' }}>
        <ProgressBar
          value={budgetStatus.current}
          max={budget.monthly}
          label="Monthly Budget Usage"
          color={budgetStatus.isOverBudget ? '#dc3545' : budgetStatus.isNearLimit ? '#ffc107' : '#28a745'}
        />
      </div>

      {/* Charts Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Usage & Cost Trend */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>Usage & Cost Trend</h3>
            <ExportButton data={data.usageSeries} filename="gpt-usage-series" />
          </div>
          {loading ? (
            <LoadingChart height={300} />
          ) : data.usageSeries.length === 0 ? (
            <EmptyState message="No usage data" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={data.usageSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString()}
                  style={{ fontSize: '.7rem' }}
                />
                <YAxis yAxisId="left" style={{ fontSize: '.7rem' }} />
                <YAxis yAxisId="right" orientation="right" style={{ fontSize: '.7rem' }} />
                <Tooltip 
                  labelFormatter={(v) => new Date(v).toLocaleString()}
                  contentStyle={{ fontSize: '.75rem' }}
                />
                <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                <Bar yAxisId="left" dataKey="count" fill="#007bff" name="Prompts" />
                <Line yAxisId="right" type="monotone" dataKey="totalCost" stroke="#28a745" strokeWidth={2} name="Cost ($)" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Prompt Type Distribution */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Prompt Types</h3>
          {loading ? (
            <LoadingChart height={300} />
          ) : data.promptTypeDistribution.length === 0 ? (
            <EmptyState message="No prompt type data" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.promptTypeDistribution}
                  dataKey="count"
                  nameKey="promptType"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label
                >
                  {data.promptTypeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Token Forecast */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>📈 Token Usage Forecast (7 Days)</h3>
        {loading ? (
          <LoadingChart height={250} />
        ) : forecastData.length === 0 ? (
          <EmptyState message="Insufficient data for forecast" />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(v) => new Date(v).toLocaleDateString()}
                style={{ fontSize: '.7rem' }}
              />
              <YAxis style={{ fontSize: '.7rem' }} />
              <Tooltip 
                labelFormatter={(v) => new Date(v).toLocaleString()}
                contentStyle={{ fontSize: '.75rem' }}
              />
              <Legend wrapperStyle={{ fontSize: '.75rem' }} />
              <Area 
                type="monotone" 
                dataKey="totalTokens" 
                fill="#007bff" 
                stroke="#007bff"
                fillOpacity={0.3}
                name="Token Usage"
              />
              <Line 
                type="monotone" 
                dataKey={(entry) => entry.forecast ? entry.totalTokens : null}
                stroke="#ffc107"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Forecast"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Model Comparison & Top Users */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Model Comparison */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Model Cost Efficiency</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Model</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Avg Cost</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Avg Tokens</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {modelComparison.map((model, idx) => (
                  <tr key={model.model} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '0.5rem' }}>{model.model}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>${model.avgCost.toFixed(4)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{model.avgTokens}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <ProgressBar
                        value={parseFloat(model.efficiencyScore)}
                        max={100}
                        color={COLORS[idx % COLORS.length]}
                        height="6px"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Users */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>Top Users by Cost</h3>
            <ExportButton data={data.topUsers} filename="top-gpt-users" />
          </div>
          {loading ? (
            <LoadingChart height={250} />
          ) : data.topUsers.length === 0 ? (
            <EmptyState message="No user data" />
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '250px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--card-bg)' }}>
                  <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>User ID</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right' }}>Cost</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right' }}>Prompts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topUsers.map(user => (
                    <tr key={user.userId} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '0.5rem' }}>User {user.userId}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>${(user.totalCost || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(user.count || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
